import React from 'react';
import { Group, Select, Switch, Text, Tooltip } from '@mantine/core';
import type {
  BaselineCompareOption,
  BaselineVarianceSummary,
} from '../../../gantt/baselineCompareUtils';
import { GANTT_TOOLBAR_LABEL_STYLE } from './ganttToolbarStyles';
import { GanttBaselineVarianceHint } from './GanttBaselineVarianceStrip';

export interface GanttBaselineCompareControlsProps {
  enabled: boolean;
  onEnabledChange: (enabled: boolean) => void;
  baselineIndex: number | null;
  onBaselineIndexChange: (index: number) => void;
  options: BaselineCompareOption[];
  compact?: boolean;
  /** When true, hide the Compare switch (toggle lives in Tools → View). */
  hideToggle?: boolean;
  /** When Compare is on, variance analytics for the selected baseline. */
  varianceSummary?: BaselineVarianceSummary | null;
  varianceBaselineLabel?: string | null;
}

export function GanttBaselineCompareControls({
  enabled,
  onEnabledChange,
  baselineIndex,
  onBaselineIndexChange,
  options,
  compact = false,
  hideToggle = false,
  varianceSummary = null,
  varianceBaselineLabel = null,
}: GanttBaselineCompareControlsProps) {
  const selectableOptions = options.filter((o) => !o.disabled);
  const hasSelectable = selectableOptions.length > 0;
  const selectData = options.map((o) => ({
    value: String(o.index),
    label: o.label,
    disabled: Boolean(o.disabled),
  }));
  const showVarianceHint =
    enabled && hasSelectable && varianceSummary != null && Boolean(varianceBaselineLabel);

  const selectControls =
    enabled && hasSelectable ? (
      <>
        <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
          Live vs
        </Text>
        <Select
          size="xs"
          w={160}
          data={selectData}
          value={baselineIndex != null ? String(baselineIndex) : null}
          onChange={(value) => {
            if (value == null) return;
            const next = Number(value);
            if (!Number.isFinite(next)) return;
            const option = options.find((o) => o.index === next);
            if (option?.disabled) return;
            onBaselineIndexChange(next);
          }}
          allowDeselect={false}
          aria-label="Baseline to compare against"
        />
        {showVarianceHint ? (
          <GanttBaselineVarianceHint
            key={varianceBaselineLabel}
            baselineLabel={varianceBaselineLabel!}
            summary={varianceSummary!}
          />
        ) : null}
      </>
    ) : null;

  if (hideToggle) {
    if (!selectControls) return null;
    return (
      <Group gap={compact ? 6 : 8} wrap="nowrap" align="center" onClick={(e) => e.stopPropagation()}>
        {selectControls}
      </Group>
    );
  }

  return (
    <Group gap={compact ? 6 : 8} wrap="nowrap" align="center" onClick={(e) => e.stopPropagation()}>
      <Tooltip
        label={
          hasSelectable
            ? enabled
              ? 'Showing current schedule bars against the selected baseline. Turn off to hide baseline bars.'
              : 'Compare the current schedule to a saved baseline on the chart.'
            : options.length > 0
              ? 'All baselines for this project are deleted. Restore or set a new baseline to compare.'
              : 'No baseline data on this project yet. Use Set baseline first.'
        }
        multiline
        w={280}
        withArrow
      >
        <Group gap={compact ? 6 : 8} wrap="nowrap" align="center">
          <Switch
            size="xs"
            checked={enabled}
            disabled={!hasSelectable}
            onChange={(e) => onEnabledChange(e.currentTarget.checked)}
            aria-label="Compare with baseline"
            styles={{
              root: { cursor: hasSelectable ? 'pointer' : 'not-allowed' },
              track: { cursor: hasSelectable ? 'pointer' : 'not-allowed' },
              thumb: { cursor: hasSelectable ? 'pointer' : 'not-allowed' },
            }}
          />
          <Text style={GANTT_TOOLBAR_LABEL_STYLE}>Compare with baseline</Text>
        </Group>
      </Tooltip>
      {selectControls}
    </Group>
  );
}
